import React, { useEffect, useRef, useState } from "react";
import { useLightboxKeys } from "@/components/lightbox/useLightboxKeys";
import { useSwipeNav } from "@/components/lightbox/useSwipeNav";
import { useWheelZoom } from "@/components/lightbox/useWheelZoom";
import { LightboxToolbar, LightboxNav, LightboxSelectButton } from "@/components/lightbox/LightboxParts";

export const Lightbox = ({
  photo,
  onClose,
  selected = false,
  onToggleSelect = null,
  onPrev = null,
  onNext = null,
  selectLabel = "Select",
  selectedLabel = "Selected",
}) => {
  const [zoom, setZoom] = useState(1);
  const stageRef = useRef(null);
  const swipe = useSwipeNav({ zoom, onPrev, onNext });
  useWheelZoom(stageRef, { active: !!photo, setZoom });

  useEffect(() => setZoom(1), [photo?.file_id]);
  useLightboxKeys({ photo, onClose, onPrev, onNext, onToggleSelect });

  if (!photo) return null;

  const toggle = () => onToggleSelect && onToggleSelect(photo.file_id);

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col" data-testid="lightbox">
      <LightboxToolbar
        name={photo.name}
        zoom={zoom}
        onZoomIn={() => setZoom((z) => Math.min(4, z + 0.5))}
        onZoomOut={() => setZoom((z) => Math.max(1, z - 0.5))}
        onClose={onClose}
      />

      <div ref={stageRef} className="relative flex-1 overflow-auto flex items-center justify-center p-4"
        onDoubleClick={() => setZoom((z) => (z >= 3 ? 1 : z + 1))}
        onTouchStart={swipe.onTouchStart}
        onTouchEnd={swipe.onTouchEnd}>
        <img
          src={photo.preview || photo.thumb}
          alt={photo.name}
          data-testid="lightbox-image"
          decoding="async"
          fetchPriority="high"
          onError={(e) => {
            const fb = photo.fallback || photo.thumb;
            if (fb && e.target.src !== fb) e.target.src = fb;
          }}
          onClick={toggle}
          style={{ transform: `scale(${zoom})`, transition: "transform 0.25s ease-out", touchAction: "pinch-zoom" }}
          className={`max-h-full max-w-full object-contain origin-center ${onToggleSelect ? "cursor-pointer" : ""}`}
        />

        <LightboxNav onPrev={onPrev} onNext={onNext} />

        {onToggleSelect && (
          <LightboxSelectButton selected={selected} onClick={toggle}
            label={selected ? selectedLabel : selectLabel} />
        )}
      </div>
    </div>
  );
};
